import { useEffect, useState } from "react";
import axios from "axios";
import {
  FaClipboardCheck,
  FaHourglassHalf,
  FaQuestionCircle,
  FaTasks,
  FaTrash,
  FaUserClock,
} from "react-icons/fa";
import { MetricCard } from "../molecules/MetricCard";
import { API_BASE_URL } from "../../../config/api";

export const MetricsOverview = () => {
  const [requests, setRequest] = useState({
    myRequests: 0,
    pendingApproval: 0,
    ongoingHires: 0,
    completedHires: 0,
    panelistTasks: 0,
    discarded: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }

    const config = { headers: { Authorization: `Bearer ${token}` } };

    const fetchData = async () => {
      try {
        const [
          myRequestsRes,
          pendingRes,
          ongoingRes,
          completedRes,
          discardedRes,
        ] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/api/requests/count`, config),
          axios.get(
            `${API_BASE_URL}/api/requests/pending-approvals/count`,
            config
          ),
          axios.get(
            `${API_BASE_URL}/api/requests/ongoing-hires/count`,
            config
          ),
          axios.get(
            `${API_BASE_URL}/api/requests/completed-hires/count`,
            config
          ),
          axios.get(
            `${API_BASE_URL}/api/requests/discarded-hires/count`,
            config
          ),
        ]);

        setRequest({
          myRequests:
            myRequestsRes.status === "fulfilled"
              ? myRequestsRes.value.data.totalRequests
              : 0,
          pendingApproval:
            pendingRes.status === "fulfilled"
              ? pendingRes.value.data.pendingCount
              : 0,
          ongoingHires:
            ongoingRes.status === "fulfilled"
              ? ongoingRes.value.data.ongoingCount || 0
              : 0,
          completedHires:
            completedRes.status === "fulfilled"
              ? completedRes.value.data.completedCount || 0
              : 0,
          panelistTasks: 0,
          discarded:
            discardedRes.status === "fulfilled"
              ? discardedRes.value.data.discardedCount || 0
              : 0,
        });
      } catch (err) {
        console.error("Error fetching metrics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const metrics = [
    {
      title: "My Requests",
      value: requests.myRequests,
      Icon: FaQuestionCircle,
      iconColor: "text-blue-600",
      iconBg: "bg-blue-100",
    },
    {
      title: "Pending Approval",
      value: requests.pendingApproval,
      Icon: FaHourglassHalf,
      iconColor: "text-orange-600",
      iconBg: "bg-orange-100",
    },
    {
      title: "Ongoing Hires",
      value: requests.ongoingHires,
      Icon: FaUserClock,
      iconColor: "text-purple-600",
      iconBg: "bg-purple-100",
    },
    {
      title: "Completed Hires",
      value: requests.completedHires,
      Icon: FaClipboardCheck,
      iconColor: "text-green-600",
      iconBg: "bg-green-100",
    },
    {
      title: "Panelist Tasks",
      value: requests.panelistTasks,
      Icon: FaTasks,
      iconColor: "text-indigo-600",
      iconBg: "bg-indigo-100",
    },
    {
      title: "Discarded",
      value: requests.discarded,
      Icon: FaTrash,
      iconColor: "text-red-600",
      iconBg: "bg-red-100",
    },
  ];

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-32 bg-gray-100 rounded-xl animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 bg-gradient-to-b from-gray-50 to-white">
      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {metrics.map((metric, index) => (
          <div
            key={metric.title}
            className="animate-fadeInUp"
            style={{ animationDelay: `${index * 80}ms` }}
          >
            <MetricCard {...metric} />
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="mt-6 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Quick Summary
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Total active processes:{" "}
              <span className="font-bold text-yellow-600">
                {requests.myRequests +
                  requests.pendingApproval +
                  requests.ongoingHires}
              </span>
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600">
              System Active
            </span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};
